import React, { useEffect, useState } from "react";
import { Loader2, CloudSun, Thermometer, Droplets, Wind, Flame, Snowflake } from "lucide-react";

// Cocoa, FL (Brevard County / Space Coast)
const LAT = 28.3861, LON = -80.742;
const CODE = {
  0: "Clear", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast", 45: "Fog", 48: "Fog",
  51: "Light drizzle", 61: "Light rain", 63: "Rain", 65: "Heavy rain", 80: "Showers", 81: "Showers", 82: "Heavy showers", 95: "Thunderstorm", 96: "Thunderstorm",
};

export default function WeatherIntel() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    (async () => {
      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FNew_York&forecast_days=5`;
        const res = await fetch(url);
        setData(await res.json());
        setStatus("ready");
      } catch { setStatus("error"); }
    })();
  }, []);

  if (status === "loading") return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading Space Coast weather…</div>;
  if (status === "error") return <p className="text-slate-500">Weather data is temporarily unavailable.</p>;

  const c = data.current;
  const temp = Math.round(c.temperature_2m);
  const demand = temp >= 88 ? { label: "High cooling demand", icon: Flame, tone: "bg-red-100 text-red-700", note: "Expect a surge in AC repair & no-cool calls. Stock capacitors and refrigerant." }
    : temp <= 55 ? { label: "Heating demand", icon: Snowflake, tone: "bg-blue-100 text-blue-700", note: "Cooler snap — heat pump and furnace calls likely. Prep heat-mode diagnostics." }
    : { label: "Moderate demand", icon: CloudSun, tone: "bg-green-100 text-green-700", note: "Great window for maintenance & tune-up outreach to the Comfort Club." };
  const DemandIcon = demand.icon;

  return (
    <div data-testid="weather-module">
      <h1 className="text-2xl font-bold text-navy mb-1">Weather Intel</h1>
      <p className="text-slate-500 mb-5 text-sm">Live conditions for the Space Coast (Cocoa, FL) to plan crews & demand.</p>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-navy text-white rounded-xl p-6 md:col-span-1">
          <p className="text-sm text-slate-300">Right now in Cocoa, FL</p>
          <div className="text-5xl font-extrabold mt-2">{temp}°F</div>
          <p className="text-slate-300 mt-1">{CODE[c.weather_code] || "—"}</p>
          <div className="flex gap-4 mt-4 text-sm text-slate-300">
            <span className="flex items-center gap-1"><Droplets className="w-4 h-4" /> {c.relative_humidity_2m}%</span>
            <span className="flex items-center gap-1"><Wind className="w-4 h-4" /> {Math.round(c.wind_speed_10m)} mph</span>
          </div>
        </div>
        <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <div className={`inline-flex items-center gap-2 text-sm font-bold px-3 py-1 rounded-full ${demand.tone}`}><DemandIcon className="w-4 h-4" /> {demand.label}</div>
          <p className="text-slate-600 mt-3">{demand.note}</p>
          <div className="grid grid-cols-5 gap-2 mt-5">
            {data.daily.time.map((d, i) => (
              <div key={d} className="text-center bg-slate-50 rounded-lg p-2">
                <p className="text-xs text-slate-400">{new Date(d + "T12:00").toLocaleDateString("en-US", { weekday: "short" })}</p>
                <Thermometer className="w-4 h-4 text-cool mx-auto my-1" />
                <p className="text-sm font-bold text-navy">{Math.round(data.daily.temperature_2m_max[i])}°</p>
                <p className="text-xs text-slate-400">{Math.round(data.daily.temperature_2m_min[i])}°</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
